import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles, Palette, Code, Phone, Mail, AlertCircle } from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { CONTACT_INFO } from '@/lib/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FreeMockupPage() {
  const [form, setForm] = useState({ name:'', email:'', website:'', phone:'', notes:'' });
  const [status, setStatus] = useState({ state:'idle', message:'' });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setStatus({ state:'error', message:'Please fill in your name and email.' });
      return;
    }
    setStatus({ state:'loading', message:'' });
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, phone: form.phone, company: form.website,
        service: 'Free Website Mockup', message: `FREE MOCKUP REQUEST\n\nWebsite/URL: ${form.website || 'n/a'}\nNotes: ${form.notes || 'n/a'}`,
      });
      setStatus({ state:'success', message: res.data?.message || "Got it - we'll deliver your custom mockup within 3 business days." });
      setForm({ name:'', email:'', website:'', phone:'', notes:'' });
    } catch {
      setStatus({ state:'error', message:'Something went wrong. Please email hello@bp.agency directly.' });
    }
  };

  return (
    <main data-testid="free-mockup-page" className="bg-white">
      <SEO
        title="Free Custom Website Mockup - No Obligation"
        description="Get a free custom website mockup designed by our team. No obligation, no contracts. See what your website could look like before you commit."
        path="/free-mockup"
      />

      <section className="relative pt-12 lg:pt-20 pb-16 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal as="div" className="inline-flex items-center gap-2 bg-bp-amber/10 border border-bp-amber/30 rounded-full px-3 py-1.5">
              <Sparkles size={14} className="text-bp-amber"/>
              <span className="text-[12px] font-bold uppercase tracking-wider text-bp-amber">100% Free · No Obligation</span>
            </Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-5 text-[44px] sm:text-[64px] lg:text-[78px]">
              Free <span className="grad-text">custom mockup</span> of your new website.
            </Reveal>
            <Reveal as="p" delay={140} className="mt-6 text-[17px] text-bp-slate leading-relaxed max-w-xl">
              See what your new website could look like - before you spend a dollar. Our designers will create a fully-custom homepage mockup based on your goals, brand, and competitors. Delivered in 3 business days.
            </Reveal>
            <Reveal as="ul" delay={180} className="mt-7 space-y-3">
              {[
                'Custom homepage design - not a template',
                'Tailored to your industry and competitors',
                'Delivered as a designed mockup in 3 business days',
                'Zero obligation. Keep the design either way.',
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15.5px] text-bp-slate"><CheckCircle2 size={20} className="text-bp-teal flex-shrink-0 mt-0.5"/>{b}</li>
              ))}
            </Reveal>
          </div>

          <Reveal as="div" delay={150} className="lg:col-span-5">
            <form onSubmit={submit} className="rounded-3xl bg-white border border-bp-slate-200 shadow-2xl p-7 lg:p-8" data-testid="free-mockup-form">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-bp-blue-50 text-bp-blue flex items-center justify-center"><Palette size={20}/></div>
                <div>
                  <div className="font-display font-bold text-bp-ink">Request your free mockup</div>
                  <div className="text-[12.5px] text-bp-slate-soft">~60 seconds</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="m-name" className="bp-label">Your name *</label>
                  <input id="m-name" required type="text" className="bp-field" value={form.name} onChange={update('name')} placeholder="Jane Smith"/>
                </div>
                <div>
                  <label htmlFor="m-email" className="bp-label">Email *</label>
                  <input id="m-email" required type="email" className="bp-field" value={form.email} onChange={update('email')} placeholder="jane@company.com"/>
                </div>
                <div>
                  <label htmlFor="m-website" className="bp-label">Current website (if any)</label>
                  <input id="m-website" type="text" className="bp-field" value={form.website} onChange={update('website')} placeholder="yourbusiness.com"/>
                </div>
                <div>
                  <label htmlFor="m-phone" className="bp-label">Phone</label>
                  <input id="m-phone" type="tel" className="bp-field" value={form.phone} onChange={update('phone')} placeholder="(555) 123-4567"/>
                </div>
                <div>
                  <label htmlFor="m-notes" className="bp-label">Anything specific you want to see?</label>
                  <textarea id="m-notes" rows={3} className="bp-field resize-none" value={form.notes} onChange={update('notes')} placeholder="A few sentences about your goals, brand, or things you like…"/>
                </div>
              </div>
              <button type="submit" disabled={status.state === 'loading'} className="btn-primary w-full mt-6 disabled:opacity-60">
                {status.state === 'loading' ? 'Sending…' : <>Get My Free Mockup <ArrowRight size={16}/></>}
              </button>
              {status.state === 'success' && (
                <div className="mt-4 flex items-start gap-3 p-3 rounded-xl bg-bp-teal-50 border border-bp-teal/30">
                  <CheckCircle2 size={18} className="text-bp-teal mt-0.5 flex-shrink-0"/>
                  <div className="text-[13.5px] text-bp-slate">{status.message}</div>
                </div>
              )}
              {status.state === 'error' && (
                <div className="mt-4 flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-200">
                  <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0"/>
                  <div className="text-[13.5px] text-bp-ink">{status.message}</div>
                </div>
              )}
              <div className="mt-4 text-[11.5px] text-bp-slate-soft text-center">
                We respect your inbox. No spam. Just your free mockup.
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-bp-slate-50">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <div className="kicker justify-center"><span className="dot"/>How it works</div>
            <h2 className="h-display mt-3 text-[34px] sm:text-[48px]">Free mockup in <span className="grad-text">3 days.</span></h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '01', title: 'Submit the form', body: 'Tell us about your business, current site, and goals.' },
              { n: '02', title: 'We design your mockup', body: 'Our designers craft a custom homepage tailored to you.' },
              { n: '03', title: 'You decide', body: 'Keep the design either way. No obligation, no pressure.' },
            ].map((s) => (
              <div key={s.n} className="card-soft p-7">
                <div className="font-display font-extrabold text-[44px] leading-none grad-text">{s.n}</div>
                <h3 className="font-display font-bold text-[19px] text-bp-ink mt-5">{s.title}</h3>
                <p className="text-[14.5px] text-bp-slate mt-2.5 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-4 text-[14px] text-bp-slate">
              <a href={`tel:${CONTACT_INFO.phoneTel}`} className="inline-flex items-center gap-2 hover:text-bp-blue"><Phone size={14}/> {CONTACT_INFO.phone}</a>
              <span className="w-1 h-1 rounded-full bg-bp-slate-soft"/>
              <a href={`mailto:${CONTACT_INFO.email}`} className="inline-flex items-center gap-2 hover:text-bp-blue"><Mail size={14}/> {CONTACT_INFO.email}</a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
